/**
 * VuePrint v1.6.3
 * Copyright (c) 2024 Jak Guru LLC
 * Vueprint a commerical work product released under the MIT License
 * and is provided as-is with no warranty or guarantee of support.
 * Documentation: https://jakguru.github.io/vueprint/
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { g as getDebugger } from './vueprint.b84f56b4.mjs';
import { ref, computed, watch, onBeforeUnmount, inject, nextTick } from 'vue';

const debug = getDebugger("VuePrint Installer", "#41B883", "#34495E");
const mounted = ref(false);
let bus;
let cron;
let identity;
let push;
const booted = ref({
  localstorage: false,
  cron: false
});
const windowCallBacks = Object.assign(
  {},
  ...Object.keys(booted.value).map((k) => {
    const windowEvent = [k, "loaded"].join(":");
    const busEvent = ["loaded", k].join(":");
    return {
      [windowEvent]: () => {
        if (bus) {
          bus.emit(busEvent, { local: true });
        }
      }
    };
  }),
  ...Object.keys(booted.value).map((k) => {
    const windowEvent = [k, "failed"].join(":");
    const busEvent = ["failed", k].join(":");
    return {
      [windowEvent]: () => {
        if (bus) {
          bus.emit(busEvent, { local: true });
        }
      }
    };
  })
);
const busCallBacks = Object.assign(
  {},
  ...Object.keys(booted.value).map((k) => {
    const busEvent = ["loaded", k].join(":");
    return {
      [busEvent]: () => {
        booted.value[k] = true;
      }
    };
  }),
  ...Object.keys(booted.value).map((k) => {
    const busEvent = ["failed", k].join(":");
    return {
      [busEvent]: () => {
        booted.value[k] = null;
      }
    };
  })
);
const isBooted = computed(() => Object.values(booted.value).every((v) => v === true || v === null));
const dependanciesBooted = computed(() => ({
  identity: identity?.booted.value || false,
  push: push?.booted.value || false
}));
const isDependanciesBooted = computed(
  () => Object.values(dependanciesBooted.value).every((v) => v === true)
);
const isMounted = computed(() => mounted.value);
const isReady = computed(() => isBooted.value === true && isDependanciesBooted.value === true);
const updateable = computed(
  () => isBooted.value && isReady.value && mounted.value && "undefined" !== typeof push && push.appUpdatePending.value
);
const useVueprint = (hooks, _debug = false) => {
  let mountedWatchCanceller;
  let bootedWatchCanceller;
  let readyWatchCanceller;
  let identityBootedWatchCanceller;
  let pushBootedWatchCanceller;
  let authenticatedWatchCanceller;
  let pushPermissionWatchCanceller;
  const doHooksOnBootedOnTrue = () => {
    if (hooks && hooks.onBooted && hooks.onBooted.onTrue) {
      hooks.onBooted.onTrue();
    }
  };
  const doHooksOnReadyOnTrue = () => {
    if (hooks && hooks.onReady && hooks.onReady.onTrue) {
      hooks.onReady.onTrue();
    }
  };
  const doHooksOnPushPermissionsOnTrue = () => {
    if (hooks && hooks.onPushPermissions && hooks.onPushPermissions.onTrue) {
      hooks.onPushPermissions.onTrue();
    }
  };
  const doHooksOnAuthenticatedOnTrue = () => {
    if (hooks && hooks.onAuthenticated && hooks.onAuthenticated.onTrue) {
      hooks.onAuthenticated.onTrue();
    }
  };
  const doHooksOnBootedOnFalse = () => {
    if (hooks && hooks.onBooted && hooks.onBooted.onFalse) {
      hooks.onBooted.onFalse();
    }
  };
  const doHooksOnReadyOnFalse = () => {
    if (hooks && hooks.onReady && hooks.onReady.onFalse) {
      hooks.onReady.onFalse();
    }
  };
  const doHooksOnPushPermissionsOnFalse = () => {
    if (hooks && hooks.onPushPermissions && hooks.onPushPermissions.onFalse) {
      hooks.onPushPermissions.onFalse();
    }
  };
  const doHooksOnAuthenticatedOnFalse = () => {
    if (hooks && hooks.onAuthenticated && hooks.onAuthenticated.onFalse) {
      hooks.onAuthenticated.onFalse();
    }
  };
  const onMounted = () => {
    bootedWatchCanceller = watch(
      () => isBooted.value,
      (booted2) => {
        if (booted2) {
          doHooksOnBootedOnTrue();
        } else {
          doHooksOnBootedOnFalse();
        }
      },
      { immediate: true }
    );
    readyWatchCanceller = watch(
      () => isReady.value,
      (ready) => {
        if (ready) {
          doHooksOnReadyOnTrue();
        } else {
          doHooksOnReadyOnFalse();
        }
      },
      { immediate: true }
    );
    if (push) {
      pushPermissionWatchCanceller = watch(
        () => push.canPush.value,
        (permission) => {
          if (permission) {
            doHooksOnPushPermissionsOnTrue();
          } else {
            doHooksOnPushPermissionsOnFalse();
          }
        }
      );
      pushBootedWatchCanceller = watch(
        () => push.booted.value,
        (booted2) => {
          dependanciesBooted.value.push = booted2;
        },
        { immediate: true }
      );
    }
    if (identity) {
      identityBootedWatchCanceller = watch(
        () => identity.booted.value,
        (is) => {
          dependanciesBooted.value.identity = is;
        },
        { immediate: true }
      );
      authenticatedWatchCanceller = watch(identity.authenticated, (authenticated) => {
        if (authenticated) {
          doHooksOnAuthenticatedOnTrue();
        } else {
          doHooksOnAuthenticatedOnFalse();
        }
      });
    }
  };
  const doOnBeforeUnmount = () => {
    if (bootedWatchCanceller) {
      bootedWatchCanceller();
    }
    if (readyWatchCanceller) {
      readyWatchCanceller();
    }
    if (identityBootedWatchCanceller) {
      identityBootedWatchCanceller();
    }
    if (authenticatedWatchCanceller) {
      authenticatedWatchCanceller();
    }
    if (pushBootedWatchCanceller) {
      pushBootedWatchCanceller();
    }
    if (pushPermissionWatchCanceller) {
      pushPermissionWatchCanceller();
    }
  };
  let wasMounted = false;
  mountedWatchCanceller = watch(
    () => isMounted.value,
    (is) => {
      if (is) {
        wasMounted = true;
        onMounted();
      } else {
        if (wasMounted) {
          doOnBeforeUnmount();
        }
      }
    },
    { immediate: true }
  );
  onBeforeUnmount(() => {
    if (mountedWatchCanceller) {
      mountedWatchCanceller();
    }
    doOnBeforeUnmount();
  });
  return {
    // If the application is currently mounted
    mounted: isMounted,
    // If all pre-mount services have been booted
    booted: isBooted,
    // If all post-mount services have been booted
    ready: isReady,
    // If the application is updatable
    updateable
  };
};
function doApplicationMount(originalCb, rootContainer, isHydrate, namespace) {
  debug("Registering VuePrint");
  bus = this.runWithContext(() => inject("bus"));
  cron = this.runWithContext(() => inject("cron"));
  identity = this.runWithContext(() => inject("identity"));
  push = this.runWithContext(() => inject("push"));
  if (cron) {
    cron.$once("* * * * * * *", () => {
      booted.value.cron = true;
    });
    debug("Cron service starting");
    cron.start();
    debug("Cron service started");
  } else {
    debug("No cron service found");
  }
  if (window) {
    if (!window._vueprint_loaded) {
      window._vueprint_loaded = {};
    }
    for (const key in windowCallBacks) {
      window.addEventListener(key, windowCallBacks[key]);
    }
    for (const key in window._vueprint_loaded) {
      if (true === window._vueprint_loaded[key]) {
        windowCallBacks[`${key}:loaded`]();
      } else if (null === window._vueprint_loaded[key]) {
        windowCallBacks[`${key}:failed`]();
      }
    }
  }
  if (bus) {
    for (const key in busCallBacks) {
      bus.on(key, busCallBacks[key], { local: true, immediate: true });
    }
  }
  originalCb.call(this, rootContainer, isHydrate, namespace);
  nextTick(() => {
    if (identity) {
      identity.boot();
    }
    if (push) {
      push.boot();
    }
    mounted.value = true;
    debug("VuePrint Registration Complete");
  });
}
function doApplicationUnmount(originalCb) {
  mounted.value = false;
  debug("Cleaning up VuePrint");
  if (cron) {
    cron.stop();
  }
  if (window) {
    for (const key in windowCallBacks) {
      window.removeEventListener(key, windowCallBacks[key]);
    }
  }
  if (bus) {
    for (const key in busCallBacks) {
      bus.off(key, busCallBacks[key], { local: true });
    }
  }
  if (identity) {
    identity.shutdown();
  }
  if (push) {
    push.shutdown();
  }
  debug("VuePrint Cleaned Up");
  originalCb.call(this);
}
const getBootStatuses = () => ({
  isBooted: isBooted.value,
  ...booted.value,
  isDependanciesBooted: isDependanciesBooted.value,
  ...dependanciesBooted.value
});

export { doApplicationUnmount as a, doApplicationMount as d, getBootStatuses as g, useVueprint as u };
//# sourceMappingURL=vueprint.1cdd5d48.mjs.map
